import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { membersAPI, blogAPI, issuesAPI, coreAPI, eventsAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import './Admin.css';

const monthlyCharts = [
  { key: 'new_members', title: '近月新加入會員' },
  { key: 'events', title: '近月活動數量' },
  { key: 'registrations', title: '近月報名數' },
  { key: 'attendances', title: '近月簽到數' },
];

const shorten = (title) => (title && title.length > 12 ? `${title.slice(0, 12)}…` : title || '');

const axisMonth = (value) => {
  if (!value || value.length < 7) return value;
  return `${Number(value.slice(5, 7))}月`;
};

const useNarrow = () => {
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 968px)').matches);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 968px)');
    const onChange = () => setNarrow(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return narrow;
};

const MonthlyChart = ({ title, months, values }) => {
  const narrow = useNarrow();
  const data = (months || []).map((month, index) => ({
    month: axisMonth(month),
    fullMonth: month,
    value: values?.[index] || 0,
  }));
  return (
    <section className="dashboard-chart">
      <h2>{title}</h2>
      <div className="dashboard-chart-frame">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              interval={0}
              tick={{ fontSize: narrow ? 11 : 12 }}
              padding={{ left: 8, right: 8 }}
            />
            <YAxis allowDecimals={false} tick={narrow ? { fontSize: 11 } : undefined} width={narrow ? 32 : undefined} />
            <Tooltip labelFormatter={(label, payload) => payload?.[0]?.payload?.fullMonth || label} />
            <Bar dataKey="value" fill="#1B75BC" name="數量" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

const ParticipationChart = ({ rows }) => {
  const narrow = useNarrow();
  const data = (rows || []).map((row) => ({
    title: shorten(row.title),
    fullTitle: row.title,
    percent: row.rate == null ? 0 : Math.round(row.rate * 100),
    display: row.rate == null ? 'N/A' : `${Math.round(row.rate * 100)}%`,
  }));
  return (
    <section className="dashboard-chart dashboard-chart-wide">
      <h2>近期活動參與率</h2>
      {data.length === 0 ? (
        <p className="dashboard-chart-empty">暫無活動</p>
      ) : (
        <div className="dashboard-chart-frame dashboard-chart-frame-tall">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 16, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                domain={[0, 100]}
                unit="%"
                allowDecimals={false}
                ticks={[0, 25, 50, 75, 100]}
                tick={{ fontSize: narrow ? 11 : 12 }}
              />
              <YAxis
                type="category"
                dataKey="title"
                width={narrow ? 84 : 120}
                tick={narrow ? { fontSize: 11 } : undefined}
              />
              <Tooltip
                formatter={(value, name, item) => [item?.payload?.display || 'N/A', '參與率']}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullTitle || label}
              />
              <Bar dataKey="percent" fill="#E8762C" name="參與率" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    members: 0,
    activeMembers: 0,
    posts: 0,
    publishedPosts: 0,
    issues: 0,
    activeIssues: 0,
    messages: 0,
    unreadMessages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [charts, setCharts] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [chartsError, setChartsError] = useState('');

  useEffect(() => {
    fetchDashboardStats();
    fetchCharts();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);

      const [membersRes, postsRes, issuesRes, messagesRes] = await Promise.all([
        membersAPI.getAll().catch(() => ({ data: [] })),
        blogAPI.adminGetPosts().catch(() => ({ data: [] })),
        issuesAPI.adminGetAll().catch(() => ({ data: [] })),
        coreAPI.adminGetMessages().catch(() => ({ data: [] })),
      ]);

      const members = Array.isArray(membersRes.data) ? membersRes.data : membersRes.data?.results || [];
      const posts = Array.isArray(postsRes.data) ? postsRes.data : postsRes.data?.results || [];
      const issues = Array.isArray(issuesRes.data) ? issuesRes.data : issuesRes.data?.results || [];
      const messages = Array.isArray(messagesRes.data) ? messagesRes.data : messagesRes.data?.results || [];

      setStats({
        members: members.length,
        activeMembers: members.filter(m => m.is_active).length,
        posts: posts.length,
        publishedPosts: posts.filter(p => p.status === 'published').length,
        issues: issues.length,
        activeIssues: issues.filter(i => i.is_active).length,
        messages: messages.length,
        unreadMessages: messages.filter(m => !m.is_read).length,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCharts = async () => {
    try {
      setChartsLoading(true);
      setChartsError('');
      const response = await eventsAPI.dashboardStats();
      setCharts(response.data);
    } catch (error) {
      setChartsError('無法載入圖表');
    } finally {
      setChartsLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <h1>管理員儀表板</h1>
      {loading && <LoadingPage message="載入統計資料中..." />}
      {!loading && (
      <>
      <div className="dashboard-stats">
        <Link to="/admin/members" className="stat-card stat-members">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>會員管理</h3>
            <div className="stat-number">{stats.members}</div>
            <div className="stat-detail">啟用中: {stats.activeMembers}</div>
          </div>
        </Link>

        <Link to="/admin/content/blog" className="stat-card stat-blog">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <h3>文章管理</h3>
            <div className="stat-number">{stats.posts}</div>
            <div className="stat-detail">已發布: {stats.publishedPosts}</div>
          </div>
        </Link>

        <Link to="/admin/content/issues" className="stat-card stat-issues">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3>議題管理</h3>
            <div className="stat-number">{stats.issues}</div>
            <div className="stat-detail">進行中: {stats.activeIssues}</div>
          </div>
        </Link>

        <Link to="/admin/messages" className="stat-card stat-messages">
          <div className="stat-icon">💬</div>
          <div className="stat-content">
            <h3>聯絡訊息</h3>
            <div className="stat-number">{stats.messages}</div>
            <div className="stat-detail">
              未讀: <span className="highlight">{stats.unreadMessages}</span>
            </div>
          </div>
        </Link>
      </div>

      <div className="dashboard-charts">
        {chartsLoading && <p className="dashboard-chart-empty">載入圖表中...</p>}
        {chartsError && <p className="dashboard-chart-empty">{chartsError}</p>}
        {charts && (
          <>
            {monthlyCharts.map((chart) => (
              <MonthlyChart
                key={chart.key}
                title={chart.title}
                months={charts.months}
                values={charts[chart.key]}
              />
            ))}
            <ParticipationChart rows={charts.participation} />
          </>
        )}
      </div>

      <div className="dashboard-actions">
        <h2>快速操作</h2>
        <div className="quick-actions">
          <Link to="/admin/events" className="btn btn-primary">活動</Link>
          <Link to="/admin/content" className="btn btn-secondary">內容管理</Link>
          <Link to="/admin/messages" className="btn btn-secondary">查看訊息</Link>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Dashboard;
